"use client";

import { StoryTVDFeatureData } from "src/types/ContentHub/Stories";
import Image from "next/image";
import { ICONS } from "../../../variables/icons";
import Icon from "src/atoms/Icon";
import { useTranslations } from "next-intl";
import Button from "src/atoms/Button";
import { RichText } from "@sitecore-content-sdk/nextjs";
import { useEffect, useRef } from "react";

type SavingsCardProps = {
  icon: string;
  title: string;
  content: string;
};
interface StoryTVDFeatureComponentProps {
  storyTVDFeature: StoryTVDFeatureData;
  TVDTotalValueDollars?: string;
}

const FigmaSizedIcon = ({ icon }: { icon: string }) => {
  const rootRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const apply = () => {
      const root = rootRef.current;
      if (!root) return;

      const isMobile = window.matchMedia("(max-width: 767px)").matches;
      const size = isMobile ? 28 : 88;

      const wrapper = root.querySelector(
        ".icon-component",
      ) as HTMLElement | null;
      if (wrapper) {
        wrapper.style.maxHeight = "none";
        wrapper.style.maxWidth = "none";
        wrapper.style.height = `${size}px`;
        wrapper.style.width = `${size}px`;
      }

      const svg = root.querySelector("svg") as SVGElement | null;
      if (svg) {
        svg.setAttribute("width", String(size));
        svg.setAttribute("height", String(size));
        const svgEl = svg as SVGSVGElement;
        svgEl.style.width = `${size}px`;
        svgEl.style.height = `${size}px`;
        svgEl.style.maxWidth = "none";
        svgEl.style.maxHeight = "none";
        svgEl.style.display = "block";

        const firstPath = svg.querySelector("path");
        const d = firstPath?.getAttribute("d");
        if (d?.startsWith("M12 24C5.38 24")) {
          const pathEl = firstPath as SVGPathElement;
          pathEl.style.display = "none";
        }
      }
    };

    apply();
    window.addEventListener("resize", apply);
    return () => window.removeEventListener("resize", apply);
  }, []);

  return (
    <span ref={rootRef} className="flex items-center justify-center">
      <Icon icon={icon} className="text-ecolab-blue" />
    </span>
  );
};

const CategoryBlock = ({
  title,
  value,
  underlineClass,
}: {
  title: string;
  value?: string;
  underlineClass: string;
}) => {
  const t = useTranslations();
  const valueDelivered = t.has("value-delivered") ? t("value-delivered") : "";

  return (
    <div className="w-full max-w-[920px] mx-auto bg-[#E5E7EB] border border-gray-horizontal-line rounded-[8px] px-[16px] py-[18px] text-center">
      <div className="text-[#164B6E] font-medium tracking-normal text-[32px] leading-[40px]">
        {title}
      </div>

      {/* Desktop underline */}
      <div className="hidden md:flex justify-center">
        <div
          className={`mt-[10px] h-[6px] w-[180px] rounded-full ${underlineClass}`}
        />
      </div>

      {value && (
        <>
          <div className="mt-[14px] text-[#164B6E] font-bold tracking-normal text-[45px] leading-[52px] [font-family:Arial]">
            {value}
          </div>

          <div className="mt-[6px] text-[16px] leading-[24px] text-[#363B40]">
            {valueDelivered}
          </div>

          <div className="sr-only">
            <div>{value}</div>
            <div>{valueDelivered}</div>
          </div>
        </>
      )}

      {/* Mobile underline (bottom) */}
      <div className="flex md:hidden justify-center mt-[12px]">
        <div className={`h-[6px] w-[160px] rounded-full ${underlineClass}`} />
      </div>
    </div>
  );
};

const SavingsCard = ({ icon, title, content }: SavingsCardProps) => {
  if (!content) return null;

  return (
    <div className="w-full max-w-[920px] mx-auto bg-[#F8FBFE] border border-gray-horizontal-line rounded-lg px-2 py-4 md:px-12 md:py-5">
      <div className="flex items-center">
        {/* Icon */}
        <div className="shrink-0 pr-4 md:pr-16">
          <span className="flex items-center justify-center rounded-full border-ecolab-blue bg-white overflow-hidden w-[40px] h-[40px] md:w-[100px] md:h-[100px]">
            <FigmaSizedIcon icon={icon} />
          </span>
        </div>

        {/* Title + RTE (desktop), title only (mobile) */}
        <div className="flex flex-col justify-center min-w-0 gap-2 md:gap-2.5">
          <div className="text-[#164B6E] font-normal tracking-normal text-[20px] leading-[26px] md:text-[28px] md:leading-[34px]">
            {title}
          </div>

          {/*  Desktop RTE ONLY */}
          <div
            className={[
              "hidden md:block",
              "text-[20px]! leading-[28px] tracking-normal text-[#363B40]",
              "[&_p]:m-0 [&_p]:leading-[28px]",
              "[&_ul]:m-0 [&_ol]:m-0 [&_li]:m-0",
              "[&_strong]:text-[#164B6E] [&_strong]:font-bold [&_strong]:text-[28px] [&_strong]:leading-[34px]",
              "[&_b]:text-[#164B6E] [&_b]:font-bold [&_b]:text-[28px] [&_b]:leading-[34px]",
            ].join(" ")}
          >
            <RichText field={{ value: content }} />
          </div>
        </div>
      </div>

      <div
        className={[
          "mt-3 md:hidden",
          "!text-[20px] leading-[28px] tracking-normal text-[#363B40]",
          "[&_p]:m-0 [&_p]:leading-[28px]",
          "[&_ul]:m-0 [&_ol]:m-0 [&_li]:m-0",
          "[&_strong]:text-[#164B6E] [&_strong]:font-bold [&_strong]:text-[28px] [&_strong]:leading-[34px]",
          "[&_b]:text-[#164B6E] [&_b]:font-bold [&_b]:text-[28px] [&_b]:leading-[34px]",
        ].join(" ")}
      >
        <RichText field={{ value: content }} />
      </div>
    </div>
  );
};

const getColumnCount = (featuredSoulutions: string[]) => {
  const count = featuredSoulutions.filter(
    (item): item is string => typeof item === "string" && item.trim() !== "",
  ).length;

  switch (count) {
    case 0:
      return "hidden";
    case 1:
      return "lg:max-w-1/3 lg:mx-auto";
    case 2:
    case 4:
      return "lg:grid-cols-2 lg:max-w-2/3 lg:mx-auto";
    default:
      return "lg:grid-cols-3";
  }
};

const renderFeaturedSolutionLink = (href: string, text: string) => {
  return (
    <Button
      buttonType={"numbers"}
      field={{
        value: {
          href,
          text,
          target: "",
          linktype: "external",
        },
      }}
      tabIndex={0}
      aria-label={text}
      iconName="CHEVRON_BLACK_RIGHT"
      className="w-full justify-between! py-5 lg:py-7 font-medium leading-[24px]!"
    />
  );
};

export default function StoryTVDSavingsAndFeaturesFields({
  storyTVDFeature,
  TVDTotalValueDollars = "",
}: Readonly<StoryTVDFeatureComponentProps>) {
  const t = useTranslations();
  const featuredSoulutions = [
    storyTVDFeature?.FeaturedSolutionLinkURL1,
    storyTVDFeature?.FeaturedSolutionLinkURL2,
    storyTVDFeature?.FeaturedSolutionLinkURL3,
    storyTVDFeature?.FeaturedSolutionLinkURL4,
    storyTVDFeature?.FeaturedSolutionLinkURL5,
    storyTVDFeature?.FeaturedSolutionLinkURL6,
  ];

  const hasBusinessOutcomesSubsections = !!(
    storyTVDFeature?.Safety ||
    storyTVDFeature?.FoodSafety ||
    storyTVDFeature?.HumanSafety ||
    storyTVDFeature?.WaterSafety ||
    storyTVDFeature?.Brand ||
    storyTVDFeature?.BrandProtection ||
    storyTVDFeature?.DelightedGuests ||
    storyTVDFeature?.Quality
  );

  const hasOperationalPerformanceSubsections = !!(
    storyTVDFeature?.Productivity ||
    storyTVDFeature?.LaborProductivity ||
    storyTVDFeature?.LocationProductivity ||
    storyTVDFeature?.ProductOptimization ||
    storyTVDFeature?.RawMaterialOptimization ||
    storyTVDFeature?.Capacity ||
    storyTVDFeature?.AssetProtection ||
    storyTVDFeature?.Reliability ||
    storyTVDFeature?.Compliance ||
    storyTVDFeature?.RegulatoryCompliance ||
    storyTVDFeature?.IndustryCompliance
  );

  const hasEnvironmentalImpactSubsections = !!(
    storyTVDFeature?.Water ||
    storyTVDFeature?.Energy ||
    storyTVDFeature?.ElectricalEnergy ||
    storyTVDFeature?.ThermalEnergy ||
    storyTVDFeature?.GreenhouseGases ||
    storyTVDFeature?.Waste ||
    storyTVDFeature?.SolidWaste ||
    storyTVDFeature?.AirEmissions
  );

  const hasAnyCategorySubsections =
    hasBusinessOutcomesSubsections ||
    hasOperationalPerformanceSubsections ||
    hasEnvironmentalImpactSubsections;

  console.log("storyTVDFeature", storyTVDFeature);
  return (
    <div>
      <div className="mx-auto bg-white w-full lg:w-(--article-container-width) max-lg:px-4 pb-6 md:pb-10">
        {hasAnyCategorySubsections && (
          <div>
            <div id="annual-savings">
              <div className="annualSavingsTop flex items-center justify-center gap-4 md:gap-6 mt-5 mb-5 bg-ecolab-blue p-4 md:p-6">
                <div className="w-[70px] h-[28px] md:w-[98px] md:h-[39px] shrink-0">
                  <Image
                    src={"/eROI-logo-white-v2.png"}
                    alt={"eROI Logo"}
                    width={98}
                    height={39}
                    className="object-contain w-full h-full"
                  />
                </div>
                <div className="text-white tracking-normal leading-[26px] md:leading-[40px] text-[24px] md:text-[32px] font-normal text-center whitespace-nowrap">
                  {t("annual-savings")}
                </div>
              </div>
            </div>
            <div className="annualSavingsContent flex flex-col gap-6">
              {hasBusinessOutcomesSubsections && (
                <div className="flex flex-col gap-3">
                  <CategoryBlock
                    title={t("business-outcomes")}
                    value={storyTVDFeature?.TVDBusinessOutcomeDollars}
                    underlineClass="bg-[#9CA3AF]"
                  />

                  <div className="space-y-3">
                    {storyTVDFeature?.Safety && (
                      <SavingsCard
                        icon={ICONS.SAFETY}
                        title={t("business-out-come-safety")}
                        content={storyTVDFeature.Safety}
                      />
                    )}
                    {storyTVDFeature?.FoodSafety && (
                      <SavingsCard
                        icon={ICONS.FOOD_SAFETY}
                        title={t("food-safety")}
                        content={storyTVDFeature.FoodSafety}
                      />
                    )}
                    {storyTVDFeature?.HumanSafety && (
                      <SavingsCard
                        icon={ICONS.SAFETY}
                        title={t("human-safety")}
                        content={storyTVDFeature.HumanSafety}
                      />
                    )}
                    {storyTVDFeature?.WaterSafety && (
                      <SavingsCard
                        icon={ICONS.WATER_SAFETY}
                        title={t("water-safety")}
                        content={storyTVDFeature.WaterSafety}
                      />
                    )}
                    {storyTVDFeature?.Brand && (
                      <SavingsCard
                        icon={ICONS.BRAND}
                        title={t("business-out-come-brand")}
                        content={storyTVDFeature.Brand}
                      />
                    )}
                    {storyTVDFeature?.BrandProtection && (
                      <SavingsCard
                        icon={ICONS.BRAND}
                        title={t("business-out-come-brand-protection")}
                        content={storyTVDFeature.BrandProtection}
                      />
                    )}
                    {storyTVDFeature?.DelightedGuests && (
                      <SavingsCard
                        icon={ICONS.DELIGHTED_GUESTS}
                        title={t("business-out-come-brand-delighted-guests")}
                        content={storyTVDFeature.DelightedGuests}
                      />
                    )}
                    {storyTVDFeature?.Quality && (
                      <SavingsCard
                        icon={ICONS.QUALITY}
                        title={t("quality")}
                        content={storyTVDFeature.Quality}
                      />
                    )}
                  </div>
                </div>
              )}

              {hasOperationalPerformanceSubsections && (
                <div className="flex flex-col gap-3">
                  <CategoryBlock
                    title={t("operational-performance")}
                    value={storyTVDFeature?.TVDOperationalPerformanceDollars}
                    underlineClass="bg-[#0057B8]"
                  />

                  <div className="space-y-3">
                    {storyTVDFeature?.Productivity && (
                      <SavingsCard
                        icon={ICONS.PRODUCTIVITY}
                        title={t("productivity")}
                        content={storyTVDFeature.Productivity}
                      />
                    )}
                    {storyTVDFeature?.LaborProductivity && (
                      <SavingsCard
                        icon={ICONS.LABOR_PRODUCTIVITY}
                        title={t("labor-productivity")}
                        content={storyTVDFeature.LaborProductivity}
                      />
                    )}
                    {storyTVDFeature?.LocationProductivity && (
                      <SavingsCard
                        icon={ICONS.LOCATION_PRODUCTIVITY}
                        title={t("location-productivity")}
                        content={storyTVDFeature.LocationProductivity}
                      />
                    )}
                    {storyTVDFeature?.ProductOptimization && (
                      <SavingsCard
                        icon={ICONS.OPTIMIZATION}
                        title={t("product-optimization")}
                        content={storyTVDFeature.ProductOptimization}
                      />
                    )}
                    {storyTVDFeature?.RawMaterialOptimization && (
                      <SavingsCard
                        icon={ICONS.OPTIMIZATION}
                        title={t("raw-material-optimization")}
                        content={storyTVDFeature.RawMaterialOptimization}
                      />
                    )}
                    {storyTVDFeature?.Capacity && (
                      <SavingsCard
                        icon={ICONS.CAPACITY}
                        title={t("capacity")}
                        content={storyTVDFeature.Capacity}
                      />
                    )}
                    {storyTVDFeature?.AssetProtection && (
                      <SavingsCard
                        icon={ICONS.ASSET_PROTECTION}
                        title={t("asset-protection")}
                        content={storyTVDFeature.AssetProtection}
                      />
                    )}
                    {storyTVDFeature?.Reliability && (
                      <SavingsCard
                        icon={ICONS.RELIABILITY}
                        title={t("reliability")}
                        content={storyTVDFeature.Reliability}
                      />
                    )}
                    {storyTVDFeature?.Compliance && (
                      <SavingsCard
                        icon={ICONS.COMPLIANCE}
                        title={t("compliance")}
                        content={storyTVDFeature.Compliance}
                      />
                    )}
                    {storyTVDFeature?.RegulatoryCompliance && (
                      <SavingsCard
                        icon={ICONS.COMPLIANCE}
                        title={t("regulatory-compliance")}
                        content={storyTVDFeature.RegulatoryCompliance}
                      />
                    )}
                    {storyTVDFeature?.IndustryCompliance && (
                      <SavingsCard
                        icon={ICONS.COMPLIANCE}
                        title={t("industry-compliance")}
                        content={storyTVDFeature.IndustryCompliance}
                      />
                    )}
                  </div>
                </div>
              )}

              {hasEnvironmentalImpactSubsections && (
                <div className="flex flex-col gap-3">
                  <CategoryBlock
                    title={t("environmental-impact")}
                    value={storyTVDFeature?.TVDEnvironmentImpactDollars}
                    underlineClass="bg-[#0A8F46]"
                  />

                  <div className="space-y-3">
                    {storyTVDFeature?.Water && (
                      <SavingsCard
                        icon={ICONS.WATER}
                        title={t("water")}
                        content={storyTVDFeature.Water}
                      />
                    )}
                    {storyTVDFeature?.Energy && (
                      <SavingsCard
                        icon={ICONS.ENERGY}
                        title={t("energy")}
                        content={storyTVDFeature.Energy}
                      />
                    )}
                    {storyTVDFeature?.ElectricalEnergy && (
                      <SavingsCard
                        icon={ICONS.ELECTRICAL_ENERGY}
                        title={t("electrical-energy")}
                        content={storyTVDFeature.ElectricalEnergy}
                      />
                    )}
                    {storyTVDFeature?.ThermalEnergy && (
                      <SavingsCard
                        icon={ICONS.THERMAL_ENERGY}
                        title={t("thermal-energy")}
                        content={storyTVDFeature.ThermalEnergy}
                      />
                    )}
                    {storyTVDFeature?.GreenhouseGases && (
                      <SavingsCard
                        icon={ICONS.GREEN_HOUSE_GASES}
                        title={t("greenhouse-gases")}
                        content={storyTVDFeature.GreenhouseGases}
                      />
                    )}
                    {storyTVDFeature?.Waste && (
                      <SavingsCard
                        icon={ICONS.WASTE}
                        title={t("waste")}
                        content={storyTVDFeature.Waste}
                      />
                    )}
                    {storyTVDFeature?.LiquidWaste && (
                      <SavingsCard
                        icon={ICONS.WASTE}
                        title={t("liquid-waste")}
                        content={storyTVDFeature.LiquidWaste}
                      />
                    )}
                    {storyTVDFeature?.SolidWaste && (
                      <SavingsCard
                        icon={ICONS.WASTE}
                        title={t("solid-waste")}
                        content={storyTVDFeature.SolidWaste}
                      />
                    )}
                    {storyTVDFeature?.AirEmissions && (
                      <SavingsCard
                        icon={ICONS.GREEN_HOUSE_GASES}
                        title={t("air-emissions")}
                        content={storyTVDFeature.AirEmissions}
                      />
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {TVDTotalValueDollars && (
          <div className="flex max-md:flex-col md:justify-between items-center mt-5 mb-5 bg-ecolab-blue p-6">
            <div>
              <div className="text-white tracking-normal leading-[33.6px] text-[28px] font-bold mb-2">
                {t("total-value-delivered")}
              </div>
              <div className="text-white tracking-normal leading-[60px] text-[60px] font-bold [font-family:Arial]">
                {TVDTotalValueDollars}
              </div>
            </div>
            <div className="max-md:mt-6 max-md:w-full">
              <Button
                buttonType="primary-reverse"
                className="inline-flex justify-center items-center m-0! md:ml-2.5! max-md:w-full font-normal leading-[24px]"
                field={{
                  value: {
                    href: "/expertise-and-innovation/exponential-value-eroi",
                    text: t("learn-more-about-eROI"),
                    target: "",
                    linktype: "external",
                  },
                }}
                iconName=""
                tabIndex={0}
                aria-label={t("learn-more-about-eROI")}
              />
            </div>
          </div>
        )}

        {/* Additional Qualitative Explanation */}
        {storyTVDFeature?.TVDQualitativeExplanation && (
          <div className="w-full max-w-[920px] mx-auto bg-[#F8FBFE] border border-gray-horizontal-line rounded-lg px-4 py-4 md:px-12 md:py-5 mt-4 md:mt-5">
            <div
              className={[
                "text-[20px]! leading-7 tracking-normal text-[#363B40]",

                // spacing for paragraphs and lists
                "[&_p]:m-0 [&_p]:mb-2",
                "[&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mt-2",
                "[&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mt-2",
                "[&_li]:mb-1.5",
                "[&_li]:ml-4 [&_li]:mb-1.5",

                // bold styling
                "[&_strong]:text-[#164B6E] [&_strong]:font-bold",
                "[&_b]:text-[#164B6E] [&_b]:font-bold",
              ].join(" ")}
            >
              <RichText
                field={{ value: storyTVDFeature.TVDQualitativeExplanation }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Featured Solutions */}
      {storyTVDFeature &&
        (storyTVDFeature?.FeaturedSolutionsTitle ||
          storyTVDFeature?.FeaturedSolutionsSubtitle ||
          storyTVDFeature?.FeaturedSolutionLinkURL1 ||
          storyTVDFeature?.FeaturedSolutionLinkURL2 ||
          storyTVDFeature?.FeaturedSolutionLinkURL3 ||
          storyTVDFeature?.FeaturedSolutionLinkURL4 ||
          storyTVDFeature?.FeaturedSolutionLinkURL5 ||
          storyTVDFeature?.FeaturedSolutionLinkURL6) && (
          <div className="bg-light-gray border border-platinum-gray py-8 lg:py-(--global-component-spacing) mt-8 empty:hidden">
            <div className="mx-auto w-full max-w-(--sitewide-container-max-width) px-4 lg:px-(--spacing-thirtytwo)">
              {storyTVDFeature?.FeaturedSolutionsTitle && (
                <h2 className="wrap-anywhere lg:text-center text-[32px] leading-[40px] antialiased text-text-gray mt-0 tracking-normal font-normal mb-6 px-0.5 lg:px-2.5 after:content-[''] after:block after:clear-both after:mx-auto after:pt-3.75 after:w-[50%] lg:after:w-[17%] after:border-b-2 after:border-teal">
                  {storyTVDFeature?.FeaturedSolutionsTitle}
                </h2>
              )}
              {storyTVDFeature?.FeaturedSolutionsSubtitle && (
                <h3 className="wrap-anywhere lg:text-center text-text-gray text-[26px] leading-[28px] antialiased mt-0 tracking-normal font-normal mb-1 px-0.5 lg:px-2.5">
                  {storyTVDFeature?.FeaturedSolutionsSubtitle}
                </h3>
              )}
              <div
                className={`mt-5 grid gap-3 lg:gap-6 grid-cols-1 lg:items-center ${getColumnCount(
                  featuredSoulutions,
                )} empty:hidden`}
              >
                {storyTVDFeature?.FeaturedSolutionLinkURL1 &&
                  renderFeaturedSolutionLink(
                    storyTVDFeature?.FeaturedSolutionLinkURL1,
                    storyTVDFeature?.FeaturedSolutionLinkTitle1,
                  )}
                {storyTVDFeature?.FeaturedSolutionLinkURL2 &&
                  renderFeaturedSolutionLink(
                    storyTVDFeature?.FeaturedSolutionLinkURL2,
                    storyTVDFeature?.FeaturedSolutionLinkTitle2,
                  )}
                {storyTVDFeature?.FeaturedSolutionLinkURL3 &&
                  renderFeaturedSolutionLink(
                    storyTVDFeature?.FeaturedSolutionLinkURL3,
                    storyTVDFeature?.FeaturedSolutionLinkTitle3,
                  )}
                {storyTVDFeature?.FeaturedSolutionLinkURL4 &&
                  renderFeaturedSolutionLink(
                    storyTVDFeature?.FeaturedSolutionLinkURL4,
                    storyTVDFeature?.FeaturedSolutionLinkTitle4,
                  )}
                {storyTVDFeature?.FeaturedSolutionLinkURL5 &&
                  renderFeaturedSolutionLink(
                    storyTVDFeature?.FeaturedSolutionLinkURL5,
                    storyTVDFeature?.FeaturedSolutionLinkTitle5,
                  )}
                {storyTVDFeature?.FeaturedSolutionLinkURL6 &&
                  renderFeaturedSolutionLink(
                    storyTVDFeature?.FeaturedSolutionLinkURL6,
                    storyTVDFeature?.FeaturedSolutionLinkTitle6,
                  )}
              </div>
            </div>
          </div>
        )}
    </div>
  );
}
